import {
  fedapayEnvironment,
  fedapayPublicKey,
} from '../config/fedapay';

const FEDAPAY_SCRIPT_SRC = 'https://cdn.fedapay.com/checkout.js?v=1.1.7';
const SCRIPT_ID = 'fedapay-checkout-script';

/** Charge le SDK FedaPay uniquement si le provider checkout est activé (pas dans index.html). */
const loadFedaPayScript = () =>
  new Promise((resolve, reject) => {
    if (typeof window !== 'undefined' && window.FedaPay) {
      resolve(window.FedaPay);
      return;
    }

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.FedaPay));
      existing.addEventListener('error', () =>
        reject(new Error('Impossible de charger le script FedaPay'))
      );
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = FEDAPAY_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve(window.FedaPay);
    script.onerror = () =>
      reject(new Error('Impossible de charger le script FedaPay'));
    document.body.appendChild(script);
  });

/** ISO 3166-1 alpha-2 à partir d'un numéro E.164 (+228… → tg). */
const guessPhoneCountry = (phoneE164) => {
  const digits = String(phoneE164 || '').replace(/\D/g, '');
  if (digits.startsWith('228')) return 'tg';
  if (digits.startsWith('229')) return 'bj';
  if (digits.startsWith('225')) return 'ci';
  if (digits.startsWith('221')) return 'sn';
  return 'tg';
};

const buildCustomerPayload = ({ email, firstName, lastName, phone }) => {
  const customer = {};
  const trimmedEmail = (email || '').trim();
  const trimmedFirst = (firstName || '').trim();
  const trimmedLast = (lastName || '').trim();
  const trimmedPhone = (phone || '').trim();

  if (trimmedEmail) customer.email = trimmedEmail;
  if (trimmedFirst) customer.firstname = trimmedFirst;
  if (trimmedLast) customer.lastname = trimmedLast;

  if (trimmedPhone && trimmedPhone.length >= 8) {
    const normalized = trimmedPhone.startsWith('+')
      ? trimmedPhone
      : `+${trimmedPhone.replace(/\D/g, '')}`;
    customer.phone_number = {
      number: normalized,
      country: guessPhoneCountry(normalized),
    };
  }

  return Object.keys(customer).length > 0 ? customer : undefined;
};

/**
 * Ouvre le widget de paiement FedaPay (Checkout.js).
 * @returns {Promise<object|null>} transaction FedaPay en cas de succès, null sinon
 */
export async function openFedaPayCheckout({
  amount,
  description = 'Commande Kilimmanjaro',
  email,
  firstName,
  lastName,
  phone,
  publicKey = fedapayPublicKey,
  environment = fedapayEnvironment,
}) {
  if (!publicKey) {
    throw new Error('FEDAPAY_PUBLIC_KEY_MISSING');
  }

  const FedaPay = await loadFedaPayScript();
  const roundedAmount = Math.max(1, Math.round(Number(amount) || 0));
  const customer = buildCustomerPayload({
    email,
    firstName,
    lastName,
    phone,
  });

  return new Promise((resolve, reject) => {
    try {
      const widget = FedaPay.init({
        public_key: publicKey,
        environment,
        transaction: {
          amount: roundedAmount,
          description,
        },
        currency: { iso: 'XOF' },
        locale: 'fr',
        ...(customer ? { customer } : {}),
        onComplete(resp) {
          if (resp?.reason === FedaPay.DIALOG_DISMISSED) {
            resolve(null);
            return;
          }

          if (resp?.reason === FedaPay.CHECKOUT_COMPLETED && resp?.transaction) {
            resolve(resp.transaction);
            return;
          }

          reject(new Error('FEDAPAY_UNKNOWN_OUTCOME'));
        },
      });

      widget.open();
    } catch (err) {
      reject(err);
    }
  });
}
